import { css } from 'lit';
import { t } from 'src/i18n';
import { sortedByNumberAndName } from 'src/store/utilities.js';
import type { InfosecApplicationOption } from './infosecApplicationOption';
import type { InfosecCategory } from './infosecCategory';
import type { InfosecDataType } from './infosecDataType';
import type { InfosecDataItem } from './infosecDataItem';
import type { InfosecStorageUnit } from './infosecStorageUnit';
import type { InfosecAsset } from './infosecAsset';
import type { SelectDropdownOption } from '../../../library/editors/elements/select-dropdown-option';
import type { Sector } from '../../../store/types';

export const responsiveFormStyles = css`
  table {
    position: relative;
    width: 100%;
    border-collapse: collapse;
    border-bottom: 1px solid var(--borderColorOnGray);
  }

  td {
    width: auto;
    vertical-align: top;
    border-top: 1px solid var(--borderColorOnGray);
    padding: 12px 0 8px 0;
  }

  td:first-child {
    text-align: left;
    padding-right: 20px;
    display: none;
  }

  :host(.width600) td:first-child {
    display: table-cell;
  }

  td:last-child {
    padding-bottom: 12px;
  }

  d-label {
    margin-bottom: 8px;
  }

  td:first-child d-label {
    padding-top: 4px;
  }

  td:last-child d-label {
    padding-top: 0;
  }

  :host(.width600) td:last-child d-label {
    display: none;
  }

  td > * {
    display: block;
  }

  .editor {
    width: 100%;
  }
`;

export const computerTypeOptions = [
  {
    value: 'Server',
    text: t('computers.options.server'),
  },
  {
    value: 'Arbeidsstasjon',
    text: t('computers.options.workstationClient'),
  },
  {
    value: 'mobile',
    text: t('computers.options.mobileDevice'),
  },
];

export const applicationPlatformOptions = [
  {
    value: 'cloudServices',
    text: t('computers.options.cloudService'),
  },
  {
    value: 'remoteServers',
    text: t('computers.options.remoteServer'),
  },
  {
    value: 'computers',
    text: t('computers.options.localComputer'),
  },
];

export const networkTypeOptions = [
  { value: 'CABLE', text: t('computers.options.cabledNetwork') },
  { value: 'WIFI', text: t('computers.options.wirelessNetwork') },
  { value: 'BOTH', text: t('computers.options.cabledAndWireless') },
];

export const internetConnectionTypeOptions = [
  { value: 'NONE', text: t('computers.options.noInternetConnection') },
  {
    value: 'NHN',
    text: t('computers.options.nhnAsIsp'),
  },
  {
    value: 'NHNotherISP',
    text: t('computers.options.nhnOwnIsp'),
  },
  { value: 'OTHER', text: t('computers.options.internetOutsideNhn') },
];

export const applications: { [keys: string]: InfosecApplicationOption[] } = {
  medicalRecordsSystems: [
    {
      type: 'any',
      value: 'cgm',
      text: 'CGM Journal',
      provider: 'CompuGroup Medical Norway AS',
    },
    {
      type: 'cloud',
      value: 'egHano',
      text: 'EG Hano',
      provider: 'EG Hano AS',
    },
    {
      type: 'any',
      value: 'extensor',
      text: 'Extensor',
      provider: 'Extensor AS',
    },
    {
      type: 'cloud',
      value: 'hoveTotal',
      text: 'Hove Total',
      provider: 'Hove Medical Systems AS',
    },
    {
      type: 'cloud',
      value: 'infodocSky',
      text: 'Infodoc Sky',
      provider: 'Infodoc AS',
    },
    {
      type: 'standalone',
      value: 'infodocPlenario',
      text: 'Infodoc Plenario',
      provider: 'Infodoc AS',
    },
    {
      type: 'standalone',
      value: 'opusDental',
      text: 'Opus Dental',
      provider: 'Opus Systemer AS',
    },
    {
      type: 'cloud',
      value: 'opusCloud',
      text: 'Opus Cloud',
      provider: 'Opus Systemer AS',
    },
    {
      type: 'standalone',
      value: 'orthodontis',
      text: 'Orthodontis',
      provider: 'Orthodontis AS',
    },
    {
      type: 'cloud',
      value: 'patientSky',
      text: 'PatientSky',
      provider: 'PatientSky AS',
    },
    {
      type: 'cloud',
      value: 'physica',
      text: 'Physica',
      provider: 'Aspit AS',
    },
    {
      type: 'cloud',
      value: 'pridokEpj',
      text: 'Pridok EPJ',
      provider: 'Pridok AS',
    },
    {
      type: 'cloud',
      value: 'psykbase',
      text: 'Psykbase',
      provider: 'Aspit AS',
    },
    {
      type: 'standalone',
      value: 'systemX',
      text: 'System X',
      provider: 'Hove Medical Systems AS',
    },
    {
      type: 'cloud',
      value: 'webMed',
      text: 'WebMed',
      provider: 'WebMed EpJ AS',
    },
    {
      type: 'cloud',
      value: 'zilke',
      text: 'Zilke',
      provider: 'Go on Software AS',
    },
  ],
  patientDialogSystems: [
    {
      type: 'cloud',
      value: 'DDFL',
      text: 'Digital Dialog Fastlege (DDFL)',
      provider: 'Norsk helsenett',
    },
    {
      type: 'cloud',
      value: 'helseRespons',
      text: 'HelseRespons',
      provider: 'WTW AS',
    },
    {
      type: 'cloud',
      value: 'patientSky',
      text: 'PatientSky',
      provider: 'PatientSky AS',
    },
    {
      type: 'cloud',
      value: 'helsebokaPro',
      text: 'Helseboka Pro',
      provider: 'Helseboka AS',
    },
    {
      type: 'cloud',
      value: 'noteless',
      text: 'Noteless',
      provider: 'Noteless AS',
      usesArtificialIntelligence: true,
    },
    {
      type: 'cloud',
      value: 'MediVox',
      text: 'MediVox',
      provider: 'MediVox AS',
      usesArtificialIntelligence: true,
    },
    {
      type: 'cloud',
      value: 'Medbric',
      text: 'Medbric',
      provider: 'Medbric AS',
      usesArtificialIntelligence: true,
    },
  ],
  accountingServices: [
    {
      type: 'cloud',
      value: 'conta',
      text: 'Conta',
      provider: 'Conta AS',
    },
    {
      type: 'cloud',
      value: 'fiken',
      text: 'Fiken',
      provider: 'Fiken AS',
    },
    {
      type: 'cloud',
      value: 'praktikertjenesten',
      text: 'Praktikertjenesten',
      provider: 'Praktikertjenesten AS',
    },
    {
      type: 'cloud',
      value: 'tannlegeregnskap',
      text: 'Tannlegeregnskap',
      provider: 'Tannlegeregnskap AS',
    },
    {
      type: 'cloud',
      value: 'tripletex',
      text: 'Tripletex',
      provider: 'Tripletex AS',
    },
    {
      type: 'cloud',
      value: 'visma',
      text: 'Visma',
      provider: 'Visma AS',
    },
  ],
  managementSystems: [
    {
      type: 'cloud',
      value: 'convene',
      text: 'Convene',
      provider: 'Convene AS',
    },
    {
      type: 'cloud',
      value: 'dropbox',
      text: 'Dropbox',
      provider: 'Dropbox',
    },
    {
      type: 'cloud',
      value: 'googleDocs',
      text: 'Google Docs',
      provider: 'Google',
    },
    {
      type: 'cloud',
      value: 'googleDrive',
      text: 'Google Drive',
      provider: 'Google',
    },
    {
      type: 'cloud',
      value: 'iCloud',
      text: 'iCloud',
      provider: 'Apple',
    },
    {
      type: 'cloud',
      value: 'infoskjermen',
      text: 'Infoskjermen',
      provider: 'Favo AS',
    },
    {
      type: 'cloud',
      value: 'oneDrive',
      text: 'OneDrive',
      provider: 'Microsoft',
    },
    {
      type: 'cloud',
      value: 'simployer',
      text: 'Simployer',
      provider: 'Simployer AS',
    },
    {
      type: 'cloud',
      value: 'sticos',
      text: 'Sticos',
      provider: 'Sticos AS',
    },
    {
      type: 'cloud',
      value: 'tidsbanken',
      text: 'Tidsbanken',
      provider: 'Tidsbanken AS',
    },
    {
      type: 'cloud',
      value: 'trinnvis',
      text: 'Trinnvis',
      provider: 'Trinnvis AS',
    },
    {
      type: 'cloud',
      value: 'tritt',
      text: 'Tritt',
      provider: 'Tritt AS',
    },
  ],
  emailServices: [
    {
      type: 'cloud',
      value: 'Domeneshop',
      text: 'Domeneshop',
      provider: 'Domeneshop AS',
    },
    {
      type: 'cloud',
      value: 'Norsk helsenett',
      text: 'Norsk helsenett',
      provider: 'Norsk helsenett',
    },
    {
      type: 'cloud',
      value: 'One.com',
      text: 'One.com',
      provider: 'One.com',
    },
    {
      type: 'cloud',
      value: 'PRO ISP',
      text: 'PRO ISP',
      provider: 'PRO ISP AS',
    },
    {
      type: 'cloud',
      value: 'SYSE',
      text: 'SYSE',
      provider: 'SYSE AS',
    },
    {
      type: 'cloud',
      value: 'Uniweb',
      text: 'Uniweb',
      provider: 'Uniweb.no AS',
    },
    {
      type: 'cloud',
      value: 'Webhuset',
      text: 'Webhuset',
      provider: 'Webhuset AS',
    },
  ],
  webServices: [
    {
      type: 'cloud',
      value: 'Domeneshop',
      text: 'Domeneshop',
      provider: 'Domeneshop AS',
    },
    {
      type: 'cloud',
      value: 'Norsk helsenett',
      text: 'Norsk helsenett',
      provider: 'Norsk helsenett',
    },
    {
      type: 'cloud',
      value: 'One.com',
      text: 'One.com',
      provider: 'One.com',
    },
    {
      type: 'cloud',
      value: 'PRO ISP',
      text: 'PRO ISP',
      provider: 'PRO ISP AS',
    },
    {
      type: 'cloud',
      value: 'SYSE',
      text: 'SYSE',
      provider: 'SYSE AS',
    },
    {
      type: 'cloud',
      value: 'Uniweb',
      text: 'Uniweb',
      provider: 'Uniweb.no AS',
    },
    {
      type: 'cloud',
      value: 'Webhuset',
      text: 'Webhuset',
      provider: 'Webhuset AS',
    },
  ],
  socialMedia: [
    {
      type: 'cloud',
      value: 'a',
      text: 'Facebook',
      provider: 'Facebook',
    },
    {
      type: 'cloud',
      value: 'b',
      text: 'LinkedIn',
      provider: 'LinkedIn',
    },
    {
      type: 'cloud',
      value: 'c',
      text: 'Twitter',
      provider: 'Twitter',
    },
    {
      type: 'cloud',
      value: 'd',
      text: 'YouTube',
      provider: 'YouTube',
    },
    {
      type: 'cloud',
      value: 'e',
      text: 'Instagram',
      provider: 'Instagram',
    },
    {
      type: 'cloud',
      value: 'f',
      text: 'TikTok',
      provider: 'TikTok',
    },
  ],
};

export const categories: { [keys: string]: InfosecCategory } = {
  medicalRecordsSystems: {
    category: 'medicalRecordsSystems',
    label: t('computers.categories.medicalRecordsSystems.label'),
    field: '',
    applicationPlaceholder: t('computers.categories.medicalRecordsSystems.placeholder'),
    applicationAddText: t('computers.categories.medicalRecordsSystems.addText'),
    applicationOptions: applications.medicalRecordsSystems,
  },
  patientDialogSystems: {
    category: 'patientDialogSystems',
    label: t('computers.categories.patientDialogSystems.label'),
    field: 'infosec_pasientDialogSystem',
    applicationPlaceholder: t('computers.categories.patientDialogSystems.placeholder'),
    applicationAddText: t('computers.categories.patientDialogSystems.addText'),
    applicationOptions: applications.patientDialogSystems,
  },
  assetData: {
    category: 'assetData',
    label: t('computers.categories.assetData.label'),
    field: '',
  },
  managementSystems: {
    category: 'managementSystems',
    label: t('computers.categories.managementSystems.label'),
    labelSingular: t('computers.categories.managementSystems.labelSingular'),
    field: '',
    applicationPlaceholder: t('computers.categories.managementSystems.placeholder'),
    applicationAddText: t('computers.categories.managementSystems.addText'),
    applicationOptions: applications.managementSystems,
  },
  accounting: {
    category: 'accounting',
    label: t('computers.categories.accounting.label'),
    field: 'infosec_accounting',
    applicationPlaceholder: t('computers.categories.accounting.placeholder'),
    applicationAddText: t('computers.categories.accounting.addText'),
    applicationOptions: applications.accountingServices,
  },
  generalCloudServices: {
    category: 'generalCloudServices',
    label: t('computers.categories.generalCloudServices.label'),
    labelSingular: t('computers.categories.generalCloudServices.labelSingular'),
    field: '',
    applicationPlaceholder: t('computers.categories.generalCloudServices.placeholder'),
    applicationAddText: t('computers.categories.generalCloudServices.addText'),
    applicationOptions: applications.generalCloudServices,
  },
  localDocuments: {
    category: 'localDocuments',
    label: t('computers.categories.localDocuments.label'),
    field: '',
  },
  remoteDocuments: {
    category: 'remoteDocuments',
    label: t('computers.categories.remoteDocuments.label'),
    field: '',
  },
  paper: {
    category: 'paperDocuments',
    label: t('computers.categories.paper.label'),
    field: '',
  },
  emailAccounts: {
    category: 'emailAccounts',
    label: t('computers.categories.emailAccounts.label'),
    labelSingular: t('computers.categories.emailAccounts.labelSingular'),
    field: 'infosec_impersonalEmailAccounts',
  },
  emailServices: {
    category: 'emailServices',
    label: t('computers.categories.emailServices.label'),
    labelSingular: t('computers.categories.emailServices.labelSingular'),
    field: 'infosec_emailServices',
    applicationPlaceholder: t('computers.categories.emailServices.placeholder'),
    applicationAddText: t('computers.categories.emailServices.addText'),
    applicationOptions: applications.emailServices,
  },
  webServices: {
    category: 'webServices',
    label: t('computers.categories.webServices.label'),
    labelSingular: t('computers.categories.webServices.labelSingular'),
    field: '',
    applicationPlaceholder: t('computers.categories.webServices.placeholder'),
    applicationAddText: t('computers.categories.webServices.addText'),
    applicationOptions: applications.webServices,
  },
  socialMedia: {
    category: 'socialMedia',
    label: t('computers.categories.socialMedia.label'),
    labelSingular: t('computers.categories.socialMedia.labelSingular'),
    field: '',
    applicationPlaceholder: t('computers.categories.socialMedia.placeholder'),
    applicationAddText: t('computers.categories.socialMedia.addText'),
    applicationOptions: applications.socialMedia,
  },
};

export const dataTypes: InfosecDataType[] = [
  {
    type: 'patientData',
    name: t('computers.dataTypes.patientData.name'),
    text: t('computers.dataTypes.patientData.text'),
    categories: [
      categories.medicalRecordsSystems,
      categories.patientDialogSystems,
      categories.assetData,
      categories.localDocuments,
      categories.remoteDocuments,
      categories.paper,
    ],
  },
  {
    type: 'managementData',
    name: t('computers.dataTypes.managementData.name'),
    text: t('computers.dataTypes.managementData.text'),
    categories: [
      categories.accounting,
      categories.managementSystems,
      categories.localDocuments,
      categories.remoteDocuments,
      categories.paper,
    ],
  },
  {
    type: 'publicInformationAndCommunication',
    name: t('computers.dataTypes.publicInformationAndCommunication.name'),
    text: t('computers.dataTypes.publicInformationAndCommunication.text'),
    categories: [categories.emailAccounts, categories.emailServices, categories.webServices, categories.socialMedia],
  },
];

export const backupFrequencyOptions = [
  {
    value: 'never',
    text: t('computers.options.backupNever'),
  },
  {
    value: 'weeklyOrLess',
    text: t('computers.options.backupWeeklyOrLess'),
  },
  {
    value: 'lessThanDaily',
    text: t('computers.options.backupLessThanDaily'),
  },
  {
    value: 'daily',
    text: t('computers.options.backupDaily'),
  },
  {
    value: 'moreThanDaily',
    text: t('computers.options.backupMoreThanDaily'),
  },
  {
    value: 'continuously',
    text: t('computers.options.backupContinuously'),
  },
];

export const noBackupReasonOptions = [
  {
    value: 'unimportantData',
    text: t('computers.options.noBackupUnimportantData'),
  },
  {
    value: 'otherStorage',
    text: t('computers.options.noBackupOtherStorage'),
  },
];

export const purposeAndLegalBasisDefaults = {
  UNDEFINED: {
    purpose: '',
    legalBasis: '',
  },
  medicalRecordsSystems: {
    purpose: t('computers.purposeAndLegalBasis.medicalRecordsSystems.purpose'),
    legalBasis: t('computers.purposeAndLegalBasis.medicalRecordsSystems.legalBasis'),
  },
  assetData: {
    purpose: t('computers.purposeAndLegalBasis.assetData.purpose'),
    legalBasis: t('computers.purposeAndLegalBasis.assetData.legalBasis'),
  },
  patientDialogSystems: {
    purpose: t('computers.purposeAndLegalBasis.patientDialogSystems.purpose'),
    legalBasis: '',
  },
  employeeRecords: {
    purpose: t('computers.purposeAndLegalBasis.employeeRecords.purpose'),
    legalBasis: t('computers.purposeAndLegalBasis.employeeRecords.legalBasis'),
  },
  customerRecords: {
    treatments: [],
    purpose: '',
    legalBasis: '',
  },
  OTHER: {
    treatments: [],
    purpose: '',
    legalBasis: '',
  },
};

export const messagingTypes = [
  { value: 'aspitMeldingsmodul', text: 'Aspit meldingsmodul' },
  { value: 'cgmEportal', text: 'CGM ePortal' },
  { value: 'dipsCommunicator', text: 'Dips Communicator' },
  { value: 'infodocESentral', text: 'Infodoc eSentral' },
  { value: 'mediLink', text: 'MediLink' },
  { value: 'primeMonitor', text: 'Prime Monitor' },
  { value: 'vismaSamhandlingLink', text: 'Visma samhandling link' },
  { value: 'OTHER', text: t('computers.options.otherSpecify') },
];

export function changeOrAddDataItem(dataItems: InfosecDataItem[], changedItem: InfosecDataItem) {
  const changedDataItems: InfosecDataItem[] = [];
  let newItem = true;
  dataItems.forEach((dataItem) => {
    if (dataItem.uuid === changedItem.uuid) {
      changedDataItems.push(changedItem);
      newItem = false;
    } else {
      changedDataItems.push(dataItem);
    }
  });
  if (newItem) {
    changedDataItems.push(changedItem);
  }
  return changedDataItems.filter((dataItem) => {
    return dataItem.uuid !== '';
  });
}

export function getCategoryName(dataItem) {
  let result = '';
  const dataType = dataTypes.filter((type) => {
    return type.type === dataItem.dataType;
  });
  if (dataType?.length) {
    const category = dataType[0].categories.filter((category) => {
      return category.category === dataItem.category;
    });
    if (category?.length) {
      result = category[0].label;
    }
  }
  return result;
}

export function getDataTypeName(dataItem) {
  return (
    dataTypes.filter((type) => {
      return type.type === dataItem.dataType;
    })[0]?.name || t('computers.other')
  );
}

export function sortSelectedCategories(list: InfosecDataItem[]): InfosecDataItem[] {
  let dataItems: InfosecDataItem[] = [];
  dataTypes.forEach((dataType) => {
    dataType.categories.forEach((category) => {
      dataItems = dataItems.concat(
        list.filter((item) => {
          return dataType.type === item.dataType && category.category === item.category;
        }),
      );
    });
  });
  return dataItems;
}

export function getComputerName(computer) {
  let text = computer.name;
  if (computer.name === '' && computer.computerType && computer.computerType !== '') {
    const computerTypes = computerTypeOptions.filter((item) => {
      return item.value === computer.computerType;
    });
    if (computerTypes.length) {
      text = computerTypes[0].text;
    }
  }
  return text;
}

/**
 * Filters data types based on organization sector.
 * Only shows patient data types for HEALTH sector organizations.
 * Also updates categories with sector-filtered application options.
 */
export function getDataTypesBySector(sector: Sector): InfosecDataType[] {
  const sectorCategories = getCategoriesBySector(sector);

  let filteredDataTypes = dataTypes;

  if (sector !== 'HEALTH') {
    // For non-health sectors, exclude patient data types
    filteredDataTypes = dataTypes.filter((dataType) => dataType.type !== 'patientData');
  }

  // Update data types with sector-filtered categories
  return filteredDataTypes.map((dataType) => ({
    ...dataType,
    categories: dataType.categories.map((category) => {
      const categoryKey = category.category;
      return sectorCategories[categoryKey] || category;
    }),
  }));
}

/**
 * Filters data items based on organization sector.
 * Only shows patient data items for HEALTH sector organizations.
 */
export function getDataItemsBySector(dataItems: InfosecDataItem[], sector: Sector): InfosecDataItem[] {
  if (sector === 'HEALTH') {
    return dataItems;
  }

  // For non-health sectors, exclude patient data items
  return dataItems.filter((dataItem) => dataItem.dataType !== 'patientData');
}

/**
 * Filters internet connection type options based on organization sector.
 * Only shows helsenett/NHN options for HEALTH sector organizations.
 */
export function getInternetConnectionTypeOptionsBySector(sector: Sector) {
  if (sector === 'HEALTH') {
    return internetConnectionTypeOptions;
  }

  // For non-health sectors, exclude helsenett/NHN options. Filter on the stable
  // `value` code ('NHN', 'NHNotherISP') rather than the now-translated `text`,
  // so the filter works in all languages. (issue #13280)
  return internetConnectionTypeOptions.filter((option) => !option.value.includes('NHN'));
}

/**
 * Filters email service applications based on organization sector.
 * Only shows helsenett email services for HEALTH sector organizations.
 */
export function getEmailServicesBySector(sector: Sector) {
  if (sector === 'HEALTH') {
    return applications.emailServices;
  }

  // For non-health sectors, exclude helsenett email services
  return applications.emailServices.filter((service) => !service.text.toLowerCase().includes('helsenett'));
}

/**
 * Filters web service applications based on organization sector.
 * Only shows helsenett web services for HEALTH sector organizations.
 */
export function getWebServicesBySector(sector: Sector) {
  if (sector === 'HEALTH') {
    return applications.webServices;
  }

  // For non-health sectors, exclude helsenett web services
  return applications.webServices.filter((service) => !service.text.toLowerCase().includes('helsenett'));
}

/**
 * Gets sector-aware categories with filtered application options.
 */
export function getCategoriesBySector(sector: Sector) {
  const emailServices = getEmailServicesBySector(sector);
  const webServices = getWebServicesBySector(sector);

  return {
    ...categories,
    emailServices: {
      ...categories.emailServices,
      applicationOptions: emailServices,
    },
    webServices: {
      ...categories.webServices,
      applicationOptions: webServices,
    },
  };
}

export function dataItemLabels(
  dataItem: InfosecDataItem,
  computers: InfosecStorageUnit[],
  cloudServices: InfosecStorageUnit[],
  remoteServers: InfosecStorageUnit[],
  partners: SelectDropdownOption[],
  assets: InfosecAsset[],
): { primary: string; secondary: string; contextual: string } {
  // item name
  let name = '';
  if (dataItem.name) {
    name = dataItem.name;
  }
  // application name
  let applicationName = '';
  if (dataItem.application && applications[dataItem.category]) {
    const defaultApplications = applications[dataItem.category].filter((item) => {
      return item.value === dataItem.application;
    });
    if (defaultApplications.length) {
      applicationName = defaultApplications[0].text;
    } else if (dataItem.application.includes('CUSTOM-')) {
      applicationName = dataItem.application.replace('CUSTOM-', '');
    } else {
      applicationName = dataItem.application;
    }
  }
  // partner name
  let partnerName = '';
  let partnerUuid = '';
  if (dataItem.dataProcessor) {
    partnerUuid = dataItem.dataProcessor;
  }
  if (dataItem.storageUnit) {
    const storageUnits: InfosecStorageUnit[] = computers.concat(cloudServices, remoteServers).filter((unit) => {
      return unit.uuid === dataItem.storageUnit;
    });
    if (storageUnits.length) {
      partnerUuid = storageUnits[0].partnerUuid;
    }
  }
  if (partnerUuid) {
    const p = partners.filter((partner) => {
      return partner.value === partnerUuid;
    });
    if (p.length) {
      partnerName = p[0].text;
    }
  }
  // computer name
  let computerName = '';
  if (dataItem.storageUnitType === 'computers') {
    const c = computers.filter((item) => {
      return item.uuid === dataItem.storageUnit;
    });
    if (c.length) {
      computerName = getComputerName(c[0]);
    }
  }
  // asset names
  let assetNames = '';
  if (dataItem.category === 'assetData') {
    const a = assets.filter((item) => {
      return dataItem.assets.includes(item.uuid);
    });
    if (a.length) {
      sortedByNumberAndName(a).forEach((item) => {
        assetNames += item.name + ' ' + item.number + ', ';
      });
      assetNames = assetNames.slice(0, -2);
    }
  }
  // location
  let locationName = '';
  if (dataItem.category === 'paperDocuments') {
    locationName = dataItem.storageLocation;
  }
  // type
  let type = '';
  if (dataItem.dataType === 'patientData') {
    type = t('computers.labelType.patientData');
    if (dataItem.category === 'medicalRecordsSystems') {
      type = t('computers.labelType.medicalRecord');
    }
    if (dataItem.category === 'patientDialogSystems') {
      type = t('computers.labelType.supportTool');
    }
    if (dataItem.category === 'assetData') {
      type = t('computers.labelType.assetData');
    }
  }
  if (dataItem.dataType === 'managementData') {
    type = t('computers.labelType.managementData');
    if (dataItem.category === 'accounting') {
      type = t('computers.labelType.accountingData');
    }
  }
  if (dataItem.category === 'emailAccounts') {
    type = t('computers.labelType.impersonalEmailAccount');
  }
  if (dataItem.category === 'emailServices') {
    type = t('computers.labelType.emailService');
  }
  if (dataItem.category === 'webServices') {
    type = t('computers.labelType.webService');
  }
  if (dataItem.category === 'socialMedia') {
    type = t('computers.labelType.socialMedium');
  }
  // medium
  let medium = '';
  let before = false;
  if (dataItem.storageUnitType === 'computers') {
    medium = t('computers.labelMedium.localPlural');
    before = true;
    if (dataItem.category === 'medicalRecordsSystems') {
      medium = t('computers.labelMedium.localSingular');
    }
  }
  if (dataItem.storageUnitType === 'cloudServices') {
    medium = t('computers.labelMedium.inCloudService');
  }
  if (dataItem.storageUnitType === 'remoteServers') {
    medium = t('computers.labelMedium.onRemoteServer');
  }
  if (dataItem.category === 'paperDocuments') {
    medium = t('computers.labelMedium.onPaper');
  }
  if (
    dataItem.category === 'patientDialogSystems' ||
    dataItem.category === 'emailAccounts' ||
    dataItem.category === 'emailServices' ||
    dataItem.category === 'webServices' ||
    dataItem.category === 'socialMedia'
  ) {
    medium = '';
  }
  let mediumAddition = type + medium;
  if (before) {
    mediumAddition = medium + type;
  }
  let primary = '';
  if (name) {
    primary = name;
  } else if (applicationName) {
    primary = applicationName;
  } else if (partnerName) {
    primary = partnerName;
  } else if (computerName) {
    primary = computerName;
  } else if (assetNames) {
    primary = assetNames;
  } else if (locationName) {
    primary = locationName;
  }
  if (!primary) {
    primary = t('computers.unknown');
  }
  return {
    primary: primary,
    secondary: mediumAddition,
    contextual: type,
  };
}
